const DEFAULT_API_BASE_URL = "/api/backend";

class ApiError extends Error {
  constructor(message, options = {}) {
    super(message);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.retryAfter = options.retryAfter;
    this.payload = options.payload;
  }
}

function getApiBaseUrl() {
  let baseUrl = process.env.NEXT_PUBLIC_API_BASE_URL || DEFAULT_API_BASE_URL;

  if (typeof window === "undefined" && !/^https?:\/\//i.test(baseUrl)) {
    const appUrl = process.env.NEXT_PUBLIC_BASE_URL || "http://localhost:3000";
    baseUrl = `${appUrl.replace(/\/$/, "")}${baseUrl}`;
  }

  return baseUrl;
}

function joinPath(baseUrl, path) {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;

  return `${baseUrl.replace(/\/$/, "")}${normalizedPath}`;
}

function normalizeRequestBody(body) {
  if (body === undefined || body === null) {
    return undefined;
  }

  if (typeof body === "string" || body instanceof FormData || body instanceof Blob) {
    return body;
  }

  return JSON.stringify(body);
}

async function parseResponsePayload(response) {
  const contentType = response.headers.get("content-type") || "";

  if (contentType.includes("application/json")) {
    return response.json();
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  return { message: text };
}

function extractRetryAfterSeconds(response, payload) {
  const headerValue = response.headers.get("retry-after");
  const payloadValue = payload?.retryAfter ?? payload?.retry_after;
  const parsedHeader = Number.parseInt(headerValue || "", 10);
  const parsedPayload = Number.parseInt(String(payloadValue || ""), 10);

  if (Number.isFinite(parsedHeader)) {
    return parsedHeader;
  }

  if (Number.isFinite(parsedPayload)) {
    return parsedPayload;
  }

  return undefined;
}

async function request(path, options = {}) {
  const {
    method = "GET",
    body,
    headers = {},
    useApiKey = false,
    useAuth = false,
  } = options;

  const token = useAuth && typeof window !== "undefined"
    ? (() => {
      try {
        const raw = window.localStorage.getItem("dw.auth.session");
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return parsed?.token || null;
      } catch {
        return null;
      }
    })()
    : null;

  const requestHeaders = new Headers({
    Accept: "application/json",
    ...headers,
  });

  const normalizedBody = normalizeRequestBody(body);
  const shouldSendJson = normalizedBody !== undefined && typeof normalizedBody === "string";

  if (shouldSendJson && !requestHeaders.has("content-type")) {
    requestHeaders.set("content-type", "application/json");
  }

  if (useApiKey) {
    requestHeaders.set("x-dw-use-api-key", "1");
  }

  if (useAuth && token) {
    requestHeaders.set("authorization", `Bearer ${token}`);
  }

  const response = await fetch(joinPath(getApiBaseUrl(), path), {
    method,
    headers: requestHeaders,
    body: normalizedBody,
  });

  const payload = await parseResponsePayload(response);

  if (!response.ok) {
    const message =
      payload?.status?.message
      || payload?.message
      || payload?.error
      || `Request failed with status ${response.status}`;
    throw new ApiError(message, {
      status: response.status,
      code: payload?.code || payload?.errorCode,
      retryAfter: extractRetryAfterSeconds(response, payload),
      payload,
    });
  }

  return payload;
}

export function get(path, options = {}) {
  return request(path, { ...options, method: "GET" });
}

export function post(path, body, options = {}) {
  return request(path, { ...options, method: "POST", body });
}

export function put(path, body, options = {}) {
  return request(path, { ...options, method: "PUT", body });
}

export function patch(path, body, options = {}) {
  return request(path, { ...options, method: "PATCH", body });
}

export function del(path, options = {}) {
  return request(path, { ...options, method: "DELETE" });
}

export { ApiError, request };
